import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Badge, Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from "reactstrap";
import ExpenseManagerService from "../../../Services/expenseManager/ExpenseManagerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { can, operationLabel, PERMISSIONS } from "./expenseManagerUtils";

const blank = { name: "", company_id: "", parent_id: "", gl_operation_type: "", gl_category: "", is_active: true, sort_order: 0 };

export default function CategoriesTab({ meta, onChanged }) {
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  const query = useQuery({
    queryKey: ["expenseManagerCategories"],
    queryFn: ExpenseManagerService.getCategories,
  });

  const flat = useMemo(() => {
    const list = [];
    const walk = (items, depth) => {
      (items || []).forEach((item) => {
        list.push({ ...item, depth });
        walk(item.children, depth + 1);
      });
    };
    walk(query.data?.data, 0);
    return list;
  }, [query.data]);

  if (query.isLoading) return <div className="text-center py-5"><Spinner /></div>;
  if (query.isError) return <div className="alert alert-danger">{getApiErrorMessage(query.error, "Could not load categories.")}</div>;

  const canManage = can(meta, PERMISSIONS.MANAGE_CATEGORIES);

  const save = async () => {
    if (!editor.name.trim()) return;
    setSaving(true);
    const payload = {
      name: editor.name.trim(),
      company_id: editor.company_id ? Number(editor.company_id) : null,
      parent_id: editor.parent_id ? Number(editor.parent_id) : null,
      gl_operation_type: editor.gl_operation_type || null,
      gl_category: editor.gl_category || null,
      is_active: Boolean(editor.is_active),
      sort_order: Number(editor.sort_order || 0),
    };
    try {
      if (editor.id) await ExpenseManagerService.updateCategory(editor.id, payload);
      else await ExpenseManagerService.createCategory(payload);
      toast.success(editor.id ? "Category updated." : "Category created.");
      setEditor(null);
      query.refetch();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not save the category."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"? Subcategories must be removed first.`)) return;
    try {
      await ExpenseManagerService.deleteCategory(category.id);
      toast.success("Category deleted.");
      query.refetch();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not delete the category."));
    }
  };

  return (
    <div className="em-categories">
      <Row className="mb-2">
        <Col className="d-flex align-items-center gap-2">
          <span className="text-muted small">{flat.length} categories</span>
          {canManage && <Button size="sm" color="primary" onClick={() => setEditor({ ...blank, company_id: meta.companies[0]?.id || "" })}>New category</Button>}
        </Col>
      </Row>

      {query.isError && <div className="alert alert-danger mb-2">{getApiErrorMessage(query.error, "Could not load categories.")}</div>}

      <Table size="sm" className="em-table align-middle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Company</th>
            <th>GL mapping</th>
            <th>Active</th>
            {canManage && <th className="text-end">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {flat.length === 0 && <tr><td colSpan={canManage ? 5 : 4} className="text-center text-muted py-4">No categories yet.</td></tr>}
          {flat.map((c) => (
            <tr key={c.id}>
              <td>
                {"— ".repeat(c.depth)}{c.name}
                {c.is_system ? <Badge color="light" pill className="ms-1">system</Badge> : null}
              </td>
              <td>{meta.companies.find((x) => x.id === c.company_id)?.name || (c.company_id ? `#${c.company_id}` : "Global")}</td>
              <td className="text-muted small">
                {c.gl_operation_type ? operationLabel(c.gl_operation_type) : "—"}
                {c.gl_category ? ` · ${c.gl_category}` : ""}
              </td>
              <td><Badge color={c.is_active ? "success" : "secondary"} pill>{c.is_active ? "Active" : "Inactive"}</Badge></td>
              {canManage && (
                <td className="text-end text-nowrap">
                  <Button size="sm" color="link" onClick={() => setEditor({ ...c, company_id: c.company_id || "", parent_id: c.parent_id || "" })}>Edit</Button>
                  <Button size="sm" color="link" className="text-danger" onClick={() => remove(c)}>Delete</Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {editor && (
        <Modal isOpen toggle={() => setEditor(null)} backdrop="static">
          <Form onSubmit={(e) => { e.preventDefault(); save(); }}>
            <ModalHeader toggle={() => setEditor(null)}>{editor.id ? "Edit category" : "New category"}</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Name *</Label>
                <Input value={editor.name} onChange={(e) => setEditor((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Software" required />
              </FormGroup>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Company</Label>
                    <Input type="select" value={editor.company_id} onChange={(e) => setEditor((s) => ({ ...s, company_id: e.target.value }))}>
                      <option value="">Global</option>
                      {meta.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Parent category</Label>
                    <Input type="select" value={editor.parent_id} onChange={(e) => setEditor((s) => ({ ...s, parent_id: e.target.value }))}>
                      <option value="">None</option>
                      {flat.filter((c) => c.id !== editor.id).map((c) => <option key={c.id} value={c.id}>{"— ".repeat(c.depth)}{c.name}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>GL operation type</Label>
                    <Input type="select" value={editor.gl_operation_type} onChange={(e) => setEditor((s) => ({ ...s, gl_operation_type: e.target.value }))}>
                      <option value="">—</option>
                      {meta.operation_types.map((t) => <option key={t} value={t}>{operationLabel(t)}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>GL category (free text)</Label>
                    <Input value={editor.gl_category || ""} onChange={(e) => setEditor((s) => ({ ...s, gl_category: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Sort order</Label>
                    <Input type="number" min="0" value={editor.sort_order} onChange={(e) => setEditor((s) => ({ ...s, sort_order: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={6} className="d-flex align-items-center gap-2 pt-4">
                  <Input type="checkbox" checked={Boolean(editor.is_active)} onChange={(e) => setEditor((s) => ({ ...s, is_active: e.target.checked }))} style={{ width: "auto" }} />
                  <Label>Active</Label>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => setEditor(null)} disabled={saving}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving || !editor.name.trim()}>{saving ? "Saving…" : "Save category"}</Button>
            </ModalFooter>
          </Form>
        </Modal>
      )}
    </div>
  );
}